import type { ReactNode } from 'react';
import { Alert, AlertDescription, Button, Skeleton } from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { ProgramConfirmDialog } from '../programs-list/ProgramConfirmDialog';

/**
 * DEV-269 — tela "FORMULÁRIO DE ALINHAMENTO DE VAGAS" (Figma `Edit alignment
 * form`, 16077:34194). Presentational: cabeçalho fixo (marca + título NÃO
 * editável + Voltar/Salvar), estados de carregamento/erro, mensagem inline
 * do painel (AC-35) e o slot `builder` onde o container monta o
 * `SurveyCreatorWrapper` em modo restrito.
 */

export type AlignmentFormEditView = 'loading' | 'error' | 'ready';

export const ALIGNMENT_FORM_TITLE = 'FORMULÁRIO DE ALINHAMENTO DE VAGAS';
export const NO_VISIBLE_PAGE_MESSAGE =
  'O formulário precisa ter pelo menos uma página visível. Torne uma página visível antes de salvar.';

export interface AlignmentFormDeletePageRequest {
  pageTitle: string;
  questionCount: number;
}

export interface AlignmentFormEditPageProps {
  view: AlignmentFormEditView;
  /** Marca da empresa (slot — o container resolve o logo). */
  brand?: ReactNode;
  /** O construtor (slot — o container monta o `SurveyCreatorWrapper`). */
  builder?: ReactNode;
  isSaving: boolean;
  /** AC-35 — recusa no próprio painel, nunca em Sonner. */
  inlineError: string | null;
  onBack: () => void;
  onSave: () => void;
  onRetry: () => void;
  /** Borda "Voltar com alteração pendente". */
  leaveDialogOpen: boolean;
  onLeaveDialogOpenChange: (open: boolean) => void;
  onConfirmLeave: () => void;
  /** AC-44 — apagar página adicional com perguntas pede confirmação. */
  deletePageRequest: AlignmentFormDeletePageRequest | null;
  onDeletePageDialogOpenChange: (open: boolean) => void;
  onConfirmDeletePage: () => void;
}

export function AlignmentFormEditPage(props: AlignmentFormEditPageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="programs" />

      <main className="flex flex-1 flex-col bg-card">
        <header
          className="flex flex-col gap-4 border-b border-border px-4 py-5 md:flex-row md:items-center md:justify-between md:px-8"
          data-testid="alignment-form-header"
        >
          <div className="flex flex-row items-center gap-3 md:flex-1">
            {props.brand}
            <h1
              className="text-xs font-semibold uppercase tracking-wide text-foreground"
              data-testid="alignment-form-title"
            >
              {ALIGNMENT_FORM_TITLE}
            </h1>
          </div>
          <div className="flex flex-row gap-2">
            <Button
              variant="outline"
              size="lg"
              onClick={props.onBack}
              data-testid="alignment-form-back"
            >
              Voltar
            </Button>
            <Button
              size="lg"
              onClick={props.onSave}
              loading={props.isSaving}
              disabled={props.view !== 'ready' || props.isSaving}
              data-testid="alignment-form-save"
            >
              Salvar
            </Button>
          </div>
        </header>

        {props.inlineError ? (
          <div className="px-4 pt-4 md:px-8">
            <Alert variant="destructive" role="alert" data-testid="alignment-form-inline-error">
              <AlertDescription>{props.inlineError}</AlertDescription>
            </Alert>
          </div>
        ) : null}

        {props.view === 'loading' ? (
          <div
            aria-busy="true"
            aria-label="Carregando formulário de alinhamento"
            className="flex flex-1 flex-col gap-4 px-4 py-6 md:px-8"
            data-testid="alignment-form-loading"
          >
            <Skeleton className="h-8 w-full max-w-sm" />
            <Skeleton className="min-h-96 flex-1" />
          </div>
        ) : null}

        {props.view === 'error' ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-16 text-center"
            data-testid="alignment-form-error"
          >
            <p className="text-base text-muted-foreground">
              Não foi possível carregar o formulário de alinhamento.
            </p>
            <Button variant="outline" onClick={props.onRetry} data-testid="alignment-form-retry">
              Tentar novamente
            </Button>
          </div>
        ) : null}

        {props.view === 'ready' ? (
          <div className="flex-1 overflow-hidden" data-testid="alignment-form-builder">
            {props.builder}
          </div>
        ) : null}
      </main>

      <ProgramConfirmDialog
        open={props.leaveDialogOpen}
        onOpenChange={props.onLeaveDialogOpenChange}
        title="Sair sem salvar?"
        description="As alterações do formulário de alinhamento ainda não foram salvas e serão perdidas."
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        onConfirm={props.onConfirmLeave}
      />

      <ProgramConfirmDialog
        open={props.deletePageRequest !== null}
        onOpenChange={props.onDeletePageDialogOpenChange}
        title="Apagar esta página?"
        description={
          props.deletePageRequest
            ? `"${props.deletePageRequest.pageTitle}" tem ${props.deletePageRequest.questionCount} pergunta(s). Apagar a página remove também as perguntas dela.`
            : ''
        }
        confirmLabel="Apagar página"
        cancelLabel="Cancelar"
        onConfirm={props.onConfirmDeletePage}
      />
    </div>
  );
}
